import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import MapManager from '../components/MapManager';
import { getOrganizations, getMaps } from '../services/api';
import {
  useOrganizationChange,
  useGlobalContext,
} from '../contexts/GlobalContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

const MapManagerPage = ({ user, onLogout }) => {
  const [organizations, setOrganizations] = useState([]);
  const [allMaps, setAllMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { selectedOrganization } = useGlobalContext();

  // Filter maps by selected organization
  const maps = useMemo(() => {
    if (!Array.isArray(allMaps)) return [];
    if (!selectedOrganization) return allMaps;

    // selectedOrganization is the full organization object, so use its id
    const orgId = selectedOrganization.id || selectedOrganization;
    return allMaps.filter(map => map.organization == orgId); // Use == for type coercion
  }, [allMaps, selectedOrganization]);

  useEffect(() => {
    loadData();
  }, []);

  // Reload maps when organization changes
  useOrganizationChange(() => {
    loadMaps();
  }, []);

  const loadData = async () => {
    try {
      const [orgsData, mapsData] = await Promise.all([
        getOrganizations(),
        getMaps(),
      ]);
      setOrganizations(orgsData);
      // Extract results array from paginated response
      setAllMaps(mapsData?.results || mapsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMaps = async () => {
    try {
      const mapsData = await getMaps();
      // Extract results array from paginated response
      setAllMaps(mapsData?.results || mapsData || []);
    } catch (error) {
      console.error('Error loading maps:', error);
    }
  };

  const handleMapSelect = map => {
    navigate(`/maps/${map.id}`);
  };

  const refreshMaps = () => {
    loadMaps();
  };

  if (loading) {
    return (
      <DashboardLayout user={user} onLogout={onLogout}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading maps...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Box
          sx={{
            mb: 3,
            p: 3,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
              mb: 1,
            }}
          >
            Maps Management
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            Create, view, and manage your floor plan maps
          </Typography>
        </Box>
        <MapManager
          maps={maps}
          organizations={organizations}
          onMapSelect={handleMapSelect}
          onRefresh={refreshMaps}
        />
      </Box>
    </DashboardLayout>
  );
};

export default MapManagerPage;
